'use client';

import { useMemo } from 'react';
import { SectionContainer, MaskedText, SectionHeader, Button } from '@/components/ui';
import { IconCheckCircle, IconAlertTriangle, IconAlertCircle } from '@repo/svgs';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import { cn } from '@/lib/cn';
import { useAnalytics } from '@/hooks/useAnalytics';
import { BedbugHistoryContent } from './BedbugHistoryModal';

interface BedbugInfestationSectionProps {
  buildingId: string;
  buildingSlug: string;
  buildingAddress: string;
  isBlurRequired?: boolean;
  baseBedbugData?: any | null;
  baseLoading?: boolean;
  baseError?: string | null;
}

// Format date to "MMM DD, YYYY" format
const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateString;
  }
};

export function BedbugInfestationSection({
  buildingId,
  buildingSlug,
  buildingAddress,
  isBlurRequired = false,
  baseBedbugData,
  baseLoading = false,
  baseError,
}: BedbugInfestationSectionProps) {
  const analytics = useAnalytics();
  // Transform API response to component format
  const { latestEntry, allEntries } = useMemo(() => {
    if (!baseBedbugData) {
      return { latestEntry: null, allEntries: [] };
    }

    // Handle different API response formats - use first dataset
    const bedbugs = baseBedbugData.bedbugs || baseBedbugData.bedbug || baseBedbugData;
    const bedbugsArray = Array.isArray(bedbugs) ? bedbugs : [];

    // Get the first filing (most recent)
    const latestFiling = bedbugsArray.length > 0 ? bedbugsArray[0] : null;

    if (!latestFiling) {
      return { latestEntry: null, allEntries: [] };
    }

    // Check if no bedbugs: infestedDwellingUnitCount == 0 && reInfestedDwellingUnit == 0
    // Handle both camelCase and snake_case, prefer camelCase
    const infestedCount =
      latestFiling.infestedDwellingUnitCount ??
      Number(latestFiling.infested_dwelling_unit_count) ??
      0;
    const reinfestedCount =
      latestFiling.reInfestedDwellingUnit ?? Number(latestFiling.re_infested_dwelling_unit) ?? 0;
    const hasBedbugs = !(Number(infestedCount) === 0 && Number(reinfestedCount) === 0);

    const filingDate = latestFiling.filingDate || latestFiling.filing_date || latestFiling.date;

    // Transform all entries for modal - convert snake_case to camelCase
    const transformedEntries = bedbugsArray.map((entry: any) => ({
      filingDate: entry.filingDate || entry.filing_date || entry.date,
      infestedDwellingUnitCount:
        entry.infestedDwellingUnitCount ?? Number(entry.infested_dwelling_unit_count) ?? 0,
      reInfestedDwellingUnit:
        entry.reInfestedDwellingUnit ?? Number(entry.re_infested_dwelling_unit) ?? 0,
      totalDwellingUnits:
        entry.totalDwellingUnits ?? entry.ofDwellingUnits ?? Number(entry.of_dwelling_units) ?? 0,
      eradicatedDwellingUnitCount:
        entry.eradicatedDwellingUnitCount ?? Number(entry.eradicated_unit_count) ?? 0,
    }));

    return {
      latestEntry: {
        hasBedbugs,
        filingDate,
        hasData: true,
      },
      allEntries: transformedEntries,
    };
  }, [baseBedbugData]);

  // No data case
  if (!latestEntry?.hasData || baseError) {
    return (
      <div>
        <SectionHeader
          title={`Bedbug infestation history for ${buildingAddress}`}
          subtitleText="City data from past 3 years"
        />
        <div className="md:px-4 px-2">
          {isBlurRequired ? (
            <div className="bg-secondary max-w-sm rounded-xl p-4 mt-2">
              <MaskedText className="h-5 w-full !mb-0" />
            </div>
          ) : (
            <p className="text-body-2 text-primary">
              This building did not obtain bedbug infestation history from tenants at the last
              annual report.
            </p>
          )}
        </div>
      </div>
    );
  }

  const isClear = !latestEntry.hasBedbugs;
  const formattedDate = formatDate(latestEntry.filingDate);

  // Modal content function - just return the content, not the Modal wrapper
  const getBedbugHistoryContent = () => (
    <BedbugHistoryContent bedbugData={allEntries} isBlurRequired={isBlurRequired} />
  );

  return (
    <div>
      <SectionHeader
        title={`Bedbug infestation history for ${buildingAddress}`}
        subtitleText="City data from past 3 years"
      />
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-xl mt-2 md:mx-4 mx-2',
          isClear ? 'bg-success-low' : 'bg-alert-low',
        )}
      >
        {isClear ? (
          <IconCheckCircle className="w-4 h-4 text-success shrink-0" />
        ) : (
          <IconAlertCircle className="w-4 h-4 text-alert shrink-0" />
        )}
        <div className="flex-1">
          {isBlurRequired ? (
            <MaskedText className="h-5 w-full" />
          ) : isClear ? (
            <p className="text-body-2 text-success">
              No bedbugs were reported in this building's latest filing on {formattedDate}.
            </p>
          ) : (
            <p className="text-body-2 text-alert">
              Bedbugs were reported in this building's latest filing on {formattedDate}.
            </p>
          )}
        </div>
      </div>

      {/* View full history button */}
      {latestEntry.hasData && (
        <ModalTrigger
          label="Annual bedbug infestation history"
          content={getBedbugHistoryContent}
          maxWidth="md"
          onOpen={() => analytics.track('rentalreport_bedbug_historyview')}
          onClose={() => analytics.track('rentalreport_bedbug_historyview_exit')}
        >
          <Button
            variant="secondary"
            className="md:w-fit mt-4 md:mx-4 mx-2"
            disabled={isBlurRequired}
          >
            View full history
          </Button>
        </ModalTrigger>
      )}
    </div>
  );
}
